import { useCallback, useMemo, memo } from 'react';
import { FaCheck, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import { useRouter } from 'next/dist/client/router';
import Button from '../../../Button';
import CabinSelect from './CabinSelect';
import { addToTrip, saveTripRequest, setSelfBookingAllowed } from '../../../../store/slices/trip';
import {
  priceRequest,
  setStateroomTab,
  toggleViewAvailableCabinFor,
} from '../../../../store/slices/cruise';
import PriceBreakdown from '../../PriceBreakdown';
import InfoDropdownPopover from '../../../InfoDropdownPopover';
import { SearchStatus, PageLinks, TravelTypes } from '../../../../constants';
import RateRules from '../../RateRules';
import Label from '../../../Label';
import CruiseAddUpdateToBrochure from '../../AddUpdateBrochure/CruiseAddUpdateToBrochure';
import { checkIsLoggedIn } from '../../../../store/slices/user';
import RateOptionPriceDisclaimerTooltip from '../../RateOptionPriceDisclaimerTooltip';
import formatPriceRequest from '../../../../helpers/formatPriceRequest';
import withSpinner from '../../../../HOCs/withSpinner';
import CruiseRateCancellationLabel from '../CruiseRateCancellationLabel';
import formatStateroomsForRequest from '../../../../helpers/formatters/formatStateroomsForRequest';
import getPriceBreakdownKey from '../../../../helpers/getPriceBreakdownKey';
import formatTripIdForBrochure from '../../../../helpers/formatters/formatTripIdForBrochure';

const RateOption = ({
  rateOption,
  cabinCategoryCode,
  onModalClose,
  tripId,
  selectedRoomIndex,
  doShowPrice,
  resultno,
  sessionKey,
  travelOption,
  tripRoomSearchInput,
}) => {
  const { track } = useAnalytics();
  const dispatch = useDispatch();
  const router = useRouter();
  const { pageName } = router.query;
  const isBookedTripCruiseChangePage =
    pageName === PageLinks.ChangeCruise.pageName || pageName === PageLinks.AddCruise.pageName;
  const isConfirmationPage = router.pathname.indexOf('confirmation') > -1;
  const isLoggedIn = useSelector(checkIsLoggedIn);

  const { itineraryId } = useSelector((state) => state.cruise.cruiseDetails);
  const { passengers } = useSelector((store) => store.trip);
  const itinerary =
    travelOption &&
    travelOption.itineraryRows &&
    _.find(travelOption.itineraryRows, (it) => it.itineraryId === itineraryId);
  const cruiseDetails = useSelector((state) => state.cruise.cruiseDetails);
  const cabinCategories = useSelector((state) => state.cruise.cabinCategories);
  const { tripConfirmation } = useSelector((state) => state.trip);
  const { currentTripId, viewingAvailableCabinsFor } = useSelector((state) => state.cruise);
  const searchObj = useSelector((state) => state.tripSearch.searchObj.cruise);
  const staterooms = useSelector((state) => state.tripSearch.searchObj.staterooms);
  const selectedTrip = useSelector((state) => state.trip.selectedTrips[currentTripId]);
  const saveTripStatus = selectedTrip && selectedTrip.saveTripStatus;
  const { isViewingBrochure, updateBrochurePriceStatus } = useSelector((state) => state.brochure);
  const { isViewingCompare } = useSelector((state) => state.compare);
  const isRetrievingStateroomRates = updateBrochurePriceStatus === SearchStatus.SEARCHING;
  const doHavePrice = !isViewingBrochure || !isViewingCompare || rateOption.totalAmount > 0;
  const selectedRate = useMemo(() => {
    if (
      selectedTrip &&
      selectedTrip.cruiseItinerary &&
      selectedTrip.cruiseItinerary.cabins &&
      selectedTrip.cruiseItinerary.cabins[selectedRoomIndex]
    ) {
      return {
        rateOptionCode:
          selectedTrip.cruiseItinerary.cabins[selectedRoomIndex].selectedRateOption.rateOptionCode,
        cabinCategoryCode:
          selectedTrip.cruiseItinerary.cabins[selectedRoomIndex].selectedShipCabinCategory
            .cabinCategoryCode,
      };
    }
    return null;
  }, [selectedRoomIndex, selectedTrip]);

  let isConfirmation = tripConfirmation != null;
  if (isBookedTripCruiseChangePage) {
    isConfirmation = false;
  }
  const isSelectedRateOption =
    selectedRate &&
    selectedRate.rateOptionCode === rateOption.rateOptionCode &&
    selectedRate.cabinCategoryCode === cabinCategoryCode;

  const formattedRequest = {
    itineraryId,
    staterooms: [staterooms[selectedRoomIndex]],
    cabinCategoryCode,
    rateOptionCode: rateOption.rateOptionCode,
    cruiseLineId:
      cruiseDetails && cruiseDetails.cruiseLine && cruiseDetails.cruiseLine.cruiseLineId,
  };

  const doShowAvailableCabins =
    !isConfirmationPage &&
    viewingAvailableCabinsFor &&
    rateOption &&
    viewingAvailableCabinsFor.itineraryId === itineraryId &&
    viewingAvailableCabinsFor.cabinCategoryCode === cabinCategoryCode &&
    viewingAvailableCabinsFor.cabinCategoryRateOptionCode === rateOption.rateOptionCode;

  const handleShowCabinSelect = () => {
    dispatch(
      toggleViewAvailableCabinFor({
        itineraryId,
        cabinCategoryCode,
        cabinCategoryRateOptionCode: rateOption && rateOption.rateOptionCode,
      }),
    );
    track('rates.handleShowCabinSelect', {
      itineraryId,
      rateOptionCode: rateOption && rateOption.rateOptionCode,
      categoryCode: cabinCategoryCode,
    });
  };

  const handlePriceBreakdownOpen = useCallback(() => {
    const farecode = rateOption.rateOptionCode;
    const { gradeno } = rateOption;
    dispatch(
      priceRequest({
        ...formatPriceRequest(formattedRequest),
        gradeno,
        resultno,
        farecode,
        sessionKey,
      }),
    );
  }, [dispatch, rateOption, formattedRequest, resultno, sessionKey]);

  const brochure = useSelector((state) => state.brochure.brochures[tripId]);
  const doAllowBook = brochure && brochure.doAllowBook;
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;

  const priceBreakdowns = useSelector((state) => state.cruise.priceBreakdowns);

  const handleCabinNumberSelect = (
    selectedShipCabin,
    selectedCabinBedTypeCode,
    doUpdateSelectedBedType,
  ) => {
    if (isBrochurePage) {
      dispatch(setSelfBookingAllowed());
    }
    let selectedCabinBedType = null;

    if (selectedShipCabin.cabinBedTypes && selectedShipCabin.cabinBedTypes.length) {
      if (selectedCabinBedTypeCode) {
        selectedCabinBedType = _.find(
          selectedShipCabin.cabinBedTypes,
          (bt) => bt.bedTypeCode === selectedCabinBedTypeCode,
        );
      } else {
        selectedCabinBedType = _.first(selectedShipCabin.cabinBedTypes);
      }
    }

    let cabins = [];
    if (selectedTrip && selectedTrip.cruiseItinerary) {
      cabins = [...selectedTrip.cruiseItinerary.cabins];
    }

    if (!cabins[selectedRoomIndex]) {
      cabins[selectedRoomIndex] = {};
    }

    cabins[selectedRoomIndex] = {
      selectedRateOption: rateOption,
      selectedShipCabin: {
        ...selectedShipCabin,
        selectedCabinBedTypeCode,
        selectedCabinBedType,
      },
      selectedShipCabinCategory: cabinCategories.find(
        (item) => item.cabinCategoryCode === cabinCategoryCode,
      ),
      searchInput: formatStateroomsForRequest(
        staterooms[selectedRoomIndex],
        cruiseDetails.cruiseLine.cruiseLineId,
      ),
      roomIndex: selectedRoomIndex,
    };

    const getNextTab = (selectedCabins) => {
      let result;
      for (let i = 0; i < staterooms.length; i += 1) {
        if (!selectedCabins[i]) {
          result = i;
          break;
        }
      }
      return result;
    };
    if ((doAllowBook || isLoggedIn) && isBrochurePage) {
      const pricingKey = getPriceBreakdownKey(
        formatPriceRequest({
          itineraryId,
          rateOptionCode: rateOption.rateOptionCode,
          cabinCategoryCode,
          passengers,
          staterooms,
        }),
      );
      const pricing = priceBreakdowns[pricingKey] || {};
      const selectedPricing = pricing && pricing.length > 0 ? pricing[0] : null;
      cabins[selectedRoomIndex].pricing = selectedPricing;
      const trip = {
        tripId,
        trip: {
          cruiseItinerary: {
            itineraryDetail:
              itinerary && itinerary.virtuosoVoyage
                ? {
                    ...cruiseDetails,
                    virtuosoVoyage: itinerary.virtuosoVoyage,
                  }
                : cruiseDetails,
            cabins,
            mode: TravelTypes.cruise,
          },
        },
        tripRoomSearchInput,
      };
      dispatch(saveTripRequest(trip));
      return;
    }
    dispatch(
      addToTrip({
        tripId: currentTripId,
        trip: {
          cruiseItinerary: {
            itineraryDetail:
              itinerary && itinerary.virtuosoVoyage
                ? {
                    ...cruiseDetails,
                    virtuosoVoyage: itinerary.virtuosoVoyage,
                  }
                : cruiseDetails,
            cabins,
            mode: TravelTypes.cruise,
          },
        },
      }),
    );
    const nextTab = getNextTab(cabins);
    if (nextTab !== undefined) {
      dispatch(
        setStateroomTab({
          tabNumber: getNextTab(cabins),
          doScroll: true,
        }),
      );
    } else if (!isBookedTripCruiseChangePage) {
      onModalClose();
    }
    if (doUpdateSelectedBedType) {
      track('rateOption.handleCabinBedTypeChange', {
        selectedShipCabin: JSON.stringify(selectedShipCabin),
        selectedBedType: (selectedCabinBedType || {}).bedTypeName,
      });
    } else {
      // If only one stateroom input, close modal after select cabin
      if (searchObj && searchObj.filters && staterooms.length === 1) {
        onModalClose();
      }
      track('rateOption.handleCabinNumberSelect', {
        selectedShipCabin: JSON.stringify(selectedShipCabin),
      });
    }

    if (isBookedTripCruiseChangePage) {
      if (saveTripStatus === SearchStatus.SEARCHING || !tripConfirmation) return;
      const bookedCruiseSegment = _.find(
        tripConfirmation.trip.tripSegments,
        (ts) => ts.mode === TravelTypes.cruise,
      );
      const request = {
        tripId: tripConfirmation.tripId,
        ignoreUpdateTripOnSuccess: true,
        trip: {
          tripId: tripConfirmation.tripId,
          doGetCruisePricing: true,
          cruiseItinerary: {
            itineraryDetail: cruiseDetails,
            cabins,
            tripSegmentId: bookedCruiseSegment && bookedCruiseSegment.id,
          },
          doChangeItineraryInBookedTrip: pageName === PageLinks.ChangeCruise.pageName,
          doAddItineraryInBookedTrip: pageName === PageLinks.AddCruise.pageName,
        },
      };

      if (nextTab == null) {
        dispatch(saveTripRequest(request));
        onModalClose();
      }
    }
  };

  if (rateOption && rateOption.price === 0.0) return <></>;

  return (
    <>
      <div className="grid grid-cols-3">
        <div className={`mb-2 ${isConfirmation ? 'col-span-3' : 'col-span-3 md:col-span-2'}`}>
          <div className="font-bold">
            {rateOption.rateOptionName} ({rateOption.rateOptionCode})
          </div>
          {rateOption.rateOptionType && <div>{`Type: ${rateOption.rateOptionType}`}</div>}
          <p>{rateOption.notes}</p>

          <CruiseRateCancellationLabel
            itineraryId={itineraryId}
            rateOptionCode={rateOption.rateOptionCode}
            selectedRoomIndex={selectedRoomIndex}
            cruiseLineId={cruiseDetails.cruiseLine.cruiseLineId}
          />
          {(isLoggedIn || !isViewingBrochure || !isViewingCompare) && (
            <RateRules
              itineraryId={itineraryId}
              rateOptionCode={rateOption.rateOptionCode}
              tripConfirmation={tripConfirmation}
              selectedRoomIndex={selectedRoomIndex}
              cruiseLineId={cruiseDetails.cruiseLine.cruiseLineId}
              resultno={resultno}
              sessionKey={sessionKey}
            />
          )}
        </div>

        {!isConfirmation && (
          <div className="mb-5 grid grid-cols-5 col-span-3 md:col-span-1 justify-items-center md:justify-items-end gap-1">
            {tripId &&
              !isViewingCompare &&
              cabinCategories &&
              cabinCategories.find((item) => item.cabinCategoryCode === cabinCategoryCode) && (
                <CruiseAddUpdateToBrochure
                  className="mb-2 col-span-3 text-sm mt-1 justify-self-start self-start md:mt-10 mr-2"
                  tripId={formatTripIdForBrochure(
                    tripId,
                    cruiseDetails?.itineraryDepartDate,
                    cruiseDetails?.itineraryReturnDate,
                    staterooms,
                  )}
                  cabinCategory={cabinCategories.find(
                    (item) => item.cabinCategoryCode === cabinCategoryCode,
                  )}
                  rateOption={rateOption}
                  itineraryDetail={cruiseDetails}
                  selectedRoomIndex={selectedRoomIndex}
                  tripRoomSearchInput={tripRoomSearchInput}
                />
              )}
            <div className="grid grid-cols-2 text-left md:text-center inline-block col-span-5 md:col-span-2 col-start-1 md:col-start-4 min-w-full md:min-w-max auto-rows-min">
              <div className="md:col-span-2 leading-none">
                {doShowPrice &&
                  withSpinner(
                    () => (
                      <div className="text-xl font-bold mb-3">
                        {!doHavePrice ? (
                          <span>Contact us for pricing</span>
                        ) : (
                          <RateOptionPriceDisclaimerTooltip
                            rate={rateOption.totalAmount || rateOption.price}
                            isTotalAmount={!!rateOption.totalAmount}
                          />
                        )}
                      </div>
                    ),
                    isRetrievingStateroomRates,
                    'Retrieving stateroom rates',
                    'inline',
                    'xs',
                  )}
                {!isRetrievingStateroomRates && doHavePrice && doShowPrice && (
                  <InfoDropdownPopover
                    onOpen={handlePriceBreakdownOpen}
                    dropdownTextPlaceholder="Get price/breakdown"
                    alignClass=""
                    className="mb-1"
                  >
                    <PriceBreakdown
                      doShowAgentCommission={!isViewingBrochure && !isViewingCompare}
                      payload={formattedRequest}
                    />
                  </InfoDropdownPopover>
                )}
              </div>
              <div className="md:flex justify-between items-center md:col-span-2 text-right md:text-left flex-row md:flex-col">
                <Button size={Button.buttonSizes.SM} onClick={handleShowCabinSelect}>
                  <>
                    <span>{isViewingBrochure || isViewingCompare ? 'View cabins' : 'Select'}</span>
                    {!doShowAvailableCabins && <FaChevronDown className="inline-block ml-1" />}
                    {doShowAvailableCabins && <FaChevronUp className="inline-block ml-1" />}
                  </>
                </Button>
              </div>
              <div className="col-span-2">
                <Label
                  text="Selected"
                  level="success"
                  icon={<FaCheck />}
                  doShow={isSelectedRateOption}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {doShowAvailableCabins && itineraryId && cabinCategoryCode && rateOption.rateOptionCode && (
        <CabinSelect
          itineraryId={itineraryId}
          cabinCategoryCode={cabinCategoryCode}
          cabinCategoryRateOptionCode={rateOption.rateOptionCode}
          onSelect={
            !(isBrochurePage && (doAllowBook || isLoggedIn)) &&
            (isViewingBrochure || isViewingCompare)
              ? null
              : handleCabinNumberSelect
          }
          isSelectedRateOption={isSelectedRateOption}
          selectedRoomIndex={selectedRoomIndex}
          resultno={resultno}
          gradeno={rateOption.gradeno}
          sessionKey={sessionKey}
          doAllowBookFromBrochure={isBrochurePage && (doAllowBook || isLoggedIn)}
        />
      )}
    </>
  );
};

RateOption.propTypes = {
  rateOption: PropTypes.shape({
    nonRefundTypeCode: PropTypes.string,
    nonRefundTypeName: PropTypes.string,
    notes: PropTypes.string,
    passengerPricing: PropTypes.arrayOf(PropTypes.shape({})),
    price: PropTypes.number,
    rateOptionCode: PropTypes.string.isRequired,
    rateOptionName: PropTypes.string,
    rateOptionType: PropTypes.string,
    totalAmount: PropTypes.number,
    gradeno: PropTypes.string,
  }).isRequired,
  cabinCategoryCode: PropTypes.string.isRequired,
  resultno: PropTypes.string.isRequired,
  sessionKey: PropTypes.string.isRequired,
  onModalClose: PropTypes.func,
  tripId: PropTypes.string,
  selectedRoomIndex: PropTypes.number.isRequired,
  doShowPrice: PropTypes.bool,
  travelOption: PropTypes.shape({
    itineraryRows: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

RateOption.defaultProps = {
  onModalClose: undefined,
  tripId: undefined,
  tripRoomSearchInput: undefined,
  doShowPrice: true,
};

export default memo(RateOption);
